<template>
  <ui-modal-layout :title="tr('Post details')" :icon="NewspaperIcon">
    <social-page
      v-if="post.source_profile"
      :source-profile="post.source_profile"
      :date="post.created_at"
    />

    <div class="flex flex-col gap-3">
      <inline-social-content :entity="post" />
      <a
        v-if="post.url"
        :href="post.url"
        target="_blank"
        class="inline-flex w-fit items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
      >
        <external-link-icon class="size-4" />
        {{ externalPostLabel }}
      </a>
    </div>

    <section v-if="metricsCards.length" class="flex flex-col gap-3">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Post metrics') }}</h3>
      <div class="grid grid-cols-2 gap-2.5">
        <div v-for="card in metricsCards" :key="card.key" class="glass-tile flex flex-col gap-1 rounded-xl p-3">
          <span class="text-xs font-semibold text-muted-foreground">{{ card.label }}</span>
          <span class="text-lg font-bold text-foreground tabular-nums">{{ card.value }}</span>
        </div>
      </div>
    </section>
  </ui-modal-layout>
</template>

<script>
import { ExternalLink, Newspaper } from '@lucide/vue';
import { markRaw } from 'vue';

import Post from '@/entities/Post.js';
import InlineSocialContent from '@/ui/components/InlineSocialContent.vue';
import SocialPage from '@/ui/components/SocialPage.vue';
import { formatCompactNumber } from '@/utils/number.js';

export default {
  name: 'ReportPostModal',
  components: {
    ExternalLinkIcon: ExternalLink,
    InlineSocialContent,
    SocialPage,
  },
  props: {
    // The post entity already attached to the report ad (read-only snapshot).
    post: {
      type: Post,
      required: true,
    },
  },
  data: () => ({
    NewspaperIcon: markRaw(Newspaper),
  }),
  computed: {
    externalPostLabel() {
      switch (this.post.platform) {
        case 'instagram':
          return this.tr('View on Instagram');

        case 'facebook':
          return this.tr('View on Facebook');

        default:
          return this.tr('View on platform');
      }
    },
    metricsCards() {
      const m = this.post.metric;
      if (!m) {
        return [];
      }

      const defs = [
        { key: 'reach', label: this.tr('Reach'), value: m.reach },
        { key: 'impressions', label: this.tr('Impressions'), value: m.impressions },
        { key: 'clicks', label: this.tr('Clicks'), value: m.clicks },
        { key: 'reactions', label: this.tr('Reactions'), value: m.reactions },
        { key: 'shares', label: this.tr('Shares'), value: m.shares },
        { key: 'video_views', label: this.tr('Video views'), value: m.video_views },
      ];

      return defs
        .filter(d => d.value !== null && d.value !== undefined)
        .map(d => ({ key: d.key, label: d.label, value: formatCompactNumber(d.value) }));
    },
  },
};
</script>
